import { useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { Consultation } from "../lib/api";
import { shortId } from "../lib/format";
import {
  CONSULT_STATUS_LABEL,
  CONSULT_TOPIC_LABEL,
  CONTACT_MODE_LABEL,
  canReviewConsultations,
  consultStatusBadge,
  dateTimeLo,
  isHttpLink,
  isoToLocalInput,
  localInputToIso,
  meetingPlaceLabel,
  meetingPlaceRequired,
  placeLabel,
} from "../lib/services";
import { AlertTriangle, CalendarIcon, Check, ChevronLeft, ExternalLink, Lock, Phone, X } from "../components/icons";

const MAX_NOTE = 2000;
const MAX_PLACE = 500;

type Action = "schedule" | "complete" | "decline";

async function fetchConsultation(id: string): Promise<Consultation> {
  // เปิดอ่านหนึ่งครั้ง = server บันทึก audit consultation.viewed (ไม่มีเนื้อหาใน audit)
  const { data, error, response } = await api.GET("/v1/admin/consultations/{id}", {
    params: { path: { id } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

class ConflictError extends Error {}

/**
 * รายละเอียดนัดปรึกษา (ADR-0027) — เนื้อหาลับ: ไม่ cache ข้ามหน้า (gcTime 0) และไม่ refetch เมื่อสลับหน้าต่าง
 * เพื่อไม่ให้เกิด audit `consultation.viewed` ซ้ำโดยไม่ได้ตั้งใจ
 */
export function ConsultationDetailPage() {
  const { id = "" } = useParams();
  const reviewer = canReviewConsultations();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["consultations", "detail", id],
    queryFn: () => fetchConsultation(id),
    enabled: !!id && reviewer,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const [action, setAction] = useState<Action | null>(null);
  // null = ยังไม่ได้แก้ ใช้ค่าปัจจุบันของนัด (เลื่อนนัดเริ่มจากเวลา/สถานที่เดิม)
  const [whenDraft, setWhenDraft] = useState<string | null>(null);
  const [placeDraft, setPlaceDraft] = useState<string | null>(null);
  const [note, setNote] = useState("");
  // เวลานัดต้องอยู่ในอนาคต — ตรวจตอนพิมพ์ (event handler) ไม่ใช่ตอน render
  const [whenPast, setWhenPast] = useState(false);

  const c = detail.data;
  const when = whenDraft ?? isoToLocalInput(c?.scheduled_at);
  const place = placeDraft ?? c?.meeting_place ?? "";

  const run = useMutation<Consultation, Error, Action>({
    mutationFn: async (a) => {
      if (!c) throw new Error("ບໍ່ພົບການນັດນີ້");
      const path = { params: { path: { id: c.id } }, headers: authHeaders() };
      const res =
        a === "schedule"
          ? await api.POST("/v1/admin/consultations/{id}/schedule", {
              ...path,
              body: {
                scheduled_at: localInputToIso(when) ?? "",
                meeting_place: place.trim(),
                note: note.trim(),
                version: c.version,
              },
            })
          : await api.POST(a === "complete" ? "/v1/admin/consultations/{id}/complete" : "/v1/admin/consultations/{id}/decline", {
              ...path,
              body: { note: note.trim(), version: c.version },
            });
      if (res.response.status === 409) {
        throw new ConflictError("ມີຄົນອື່ນແກ້ການນັດນີ້ກ່ອນແລ້ວ — ໂຫຼດຂໍ້ມູນລ່າສຸດໃຫ້ແລ້ວ ກວດແລ້ວລອງໃໝ່");
      }
      if (res.error || !res.data) throw new Error(describeError(res.error, res.response.status));
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["consultations", "detail", id], data);
      void qc.invalidateQueries({ queryKey: ["consultations", "admin"] });
      void qc.invalidateQueries({ queryKey: ["consultations", "stats"] });
      setAction(null);
      setNote("");
      setWhenDraft(null);
      setPlaceDraft(null);
    },
    onError: (e) => {
      if (e instanceof ConflictError) void detail.refetch();
    },
  });

  if (!reviewer) {
    return (
      <div>
        <BackLink />
        <p className="lock-note" style={{ marginTop: "var(--space-4)" }}>
          <Lock size={14} /> ເນື້ອໃນການປຶກສາເປັນຄວາມລັບ — ສະເພາະຕາແສງທີ່ຮັບຜິດຊອບອ່ານໄດ້
        </p>
      </div>
    );
  }
  if (detail.isPending) {
    return (
      <div>
        <BackLink />
        <p className="caption" style={{ marginTop: "var(--space-4)" }}>
          ກຳລັງໂຫຼດ…
        </p>
      </div>
    );
  }
  if (detail.isError || !c) {
    return (
      <div>
        <BackLink />
        <div className="alert-error" style={{ marginTop: "var(--space-4)" }}>
          ໂຫຼດການນັດບໍ່ສຳເລັດ: {detail.error?.message ?? "ບໍ່ພົບການນັດນີ້"}
        </div>
      </div>
    );
  }

  const open = c.status === "requested" || c.status === "scheduled";
  const whenInFuture = localInputToIso(when) !== null && !whenPast;
  const placeOk = !meetingPlaceRequired(c.contact_mode) || place.trim().length > 0;
  const canSubmit =
    action === "schedule"
      ? whenInFuture && placeOk
      : action === "decline"
        ? note.trim().length > 0
        : action === "complete";

  function openAction(a: Action) {
    setAction(a);
    setNote("");
    setWhenPast(isPast(when));
    run.reset();
  }

  return (
    <div>
      <BackLink />
      <header className="page-header" style={{ marginTop: "var(--space-3)" }}>
        <div>
          <h1 className="page-header__title">{CONSULT_TOPIC_LABEL[c.topic]}</h1>
          <p className="page-header__subtitle">
            #<span className="num">{shortId(c.id)}</span> · ຂໍເມື່ອ <span className="num">{dateTimeLo(c.created_at)}</span>
          </p>
        </div>
        <span className={consultStatusBadge(c.status)}>{CONSULT_STATUS_LABEL[c.status]}</span>
      </header>

      <p className="lock-note" style={{ marginBottom: "var(--space-3)" }}>
        <Lock size={14} /> ຂໍ້ມູນລັບ — ການເປີດອ່ານຄັ້ງນີ້ຖືກບັນທຶກໄວ້ແລ້ວ ຫ້າມສົ່ງຕໍ່
      </p>

      <div className="grid2">
        <div className="stack">
          <section className="card">
            <h2 className="section-title">ເລື່ອງທີ່ຂໍປຶກສາ</h2>
            <p className="body-sm incident-desc" style={{ marginTop: "var(--space-2)" }}>
              {c.details || "—"}
            </p>
            <dl className="incident-facts">
              <dt>ຊ່ອງທາງທີ່ສະດວກ</dt>
              <dd>{CONTACT_MODE_LABEL[c.contact_mode]}</dd>
              <dt>ເວລາທີ່ສະດວກ</dt>
              <dd className="incident-desc">{c.preferred_times || "—"}</dd>
              <dt>ບ້ານ</dt>
              <dd>{placeLabel(c)}</dd>
            </dl>
          </section>

          <section className="card">
            <h2 className="section-title">ຜູ້ຂໍ</h2>
            {c.requester ? (
              <dl className="incident-facts">
                <dt>ຊື່</dt>
                <dd>{c.requester.full_name || "—"}</dd>
                <dt>ເບີໂທ</dt>
                <dd>
                  {c.requester.phone ? (
                    <a className="num" href={`tel:${c.requester.phone}`}>
                      <Phone size={12} /> {c.requester.phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </dl>
            ) : (
              <p className="caption" style={{ marginTop: "var(--space-2)" }}>
                ບໍ່ມີຂໍ້ມູນຕິດຕໍ່
              </p>
            )}
          </section>
        </div>

        <div className="stack">
          <section className="card">
            <h2 className="section-title">ການນັດ</h2>
            <dl className="incident-facts">
              <dt>ເວລານັດ</dt>
              <dd className="num">{c.scheduled_at ? dateTimeLo(c.scheduled_at) : "ຍັງບໍ່ໄດ້ນັດ"}</dd>
              {c.meeting_place && (
                <>
                  <dt>{meetingPlaceLabel(c.contact_mode)}</dt>
                  <dd>
                    {isHttpLink(c.meeting_place) ? (
                      <a href={c.meeting_place} target="_blank" rel="noreferrer">
                        {c.meeting_place} <ExternalLink size={12} />
                      </a>
                    ) : (
                      c.meeting_place
                    )}
                  </dd>
                </>
              )}
              {c.closed_at && (
                <>
                  <dt>{c.status === "cancelled" ? "ຜູ້ຂໍຍົກເລີກເມື່ອ" : "ປິດເມື່ອ"}</dt>
                  <dd className="num">{dateTimeLo(c.closed_at)}</dd>
                </>
              )}
            </dl>
            {c.officer_note && (
              <div style={{ marginTop: "var(--space-3)" }}>
                <div className="form-field-label">ບັນທຶກເຈົ້າໜ້າທີ່ (ຜູ້ຂໍອ່ານໄດ້)</div>
                <p className="body-sm incident-desc">{c.officer_note}</p>
              </div>
            )}
            {c.status === "cancelled" && (
              <p className="caption" style={{ marginTop: "var(--space-2)" }}>
                ຜູ້ຂໍຍົກເລີກການນັດນີ້ແລ້ວ
              </p>
            )}
          </section>

          {c.can_manage && open && (
            <section className="card">
              <h2 className="section-title">ດຳເນີນການ</h2>
              {action === null ? (
                <div className="row" style={{ flexWrap: "wrap", marginTop: "var(--space-3)" }}>
                  <button type="button" className="btn-primary" onClick={() => openAction("schedule")}>
                    <CalendarIcon size={14} /> {c.status === "scheduled" ? "ເລື່ອນນັດ" : "ນັດວັນເວລາ"}
                  </button>
                  {c.status === "scheduled" && (
                    <button type="button" className="btn-secondary" onClick={() => openAction("complete")}>
                      <Check size={14} /> ປຶກສາແລ້ວ
                    </button>
                  )}
                  <button type="button" className="btn-danger" onClick={() => openAction("decline")}>
                    <X size={14} /> ບໍ່ຮັບນັດ
                  </button>
                </div>
              ) : (
                <div className="stack" style={{ marginTop: "var(--space-3)" }}>
                  {action === "schedule" && (
                    <>
                      <div>
                        <label className="form-field-label" htmlFor="consult-when">
                          ວັນ ແລະ ເວລານັດ<span className="required"> *</span>
                        </label>
                        <input
                          id="consult-when"
                          type="datetime-local"
                          className="input-field"
                          value={when}
                          aria-invalid={when !== "" && !whenInFuture}
                          onChange={(e) => {
                            setWhenDraft(e.target.value);
                            setWhenPast(isPast(e.target.value));
                          }}
                        />
                        {when !== "" && !whenInFuture && <p className="field-error">ເວລານັດຕ້ອງຢູ່ໃນອະນາຄົດ</p>}
                      </div>
                      <div>
                        <label className="form-field-label" htmlFor="consult-place">
                          {meetingPlaceLabel(c.contact_mode)}
                          {meetingPlaceRequired(c.contact_mode) && <span className="required"> *</span>}
                        </label>
                        <input
                          id="consult-place"
                          className="input-field"
                          maxLength={MAX_PLACE}
                          placeholder={c.contact_mode === "video" ? "https://…" : ""}
                          value={place}
                          onChange={(e) => setPlaceDraft(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="form-field-label" htmlFor="consult-note">
                      {action === "decline" ? "ເຫດຜົນທີ່ບໍ່ຮັບນັດ" : "ບັນທຶກເຖິງຜູ້ຂໍ (ບໍ່ບັງຄັບ)"}
                      {action === "decline" && <span className="required"> *</span>}
                    </label>
                    <textarea
                      id="consult-note"
                      className="input-field"
                      maxLength={MAX_NOTE}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <p className="caption">ຜູ້ຂໍອ່ານບັນທຶກນີ້ໄດ້ໃນແອັບ</p>
                  </div>
                  <div className="row">
                    <button
                      type="button"
                      className={action === "decline" ? "btn-danger" : "btn-primary"}
                      disabled={!canSubmit || run.isPending}
                      onClick={() => run.mutate(action)}
                    >
                      {action === "schedule" ? "ບັນທຶກການນັດ" : action === "complete" ? "ຢືນຢັນປຶກສາແລ້ວ" : "ຢືນຢັນບໍ່ຮັບນັດ"}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setAction(null)}>
                      ຍົກເລີກ
                    </button>
                  </div>
                </div>
              )}
              {run.isError && (
                <p className="field-error" role="alert" style={{ marginTop: "var(--space-2)" }}>
                  <AlertTriangle size={14} /> {run.error.message}
                </p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** ค่าจาก datetime-local ที่ไม่อยู่ในอนาคต (ว่าง = ไม่ใช่ "อดีต" — ปุ่มถูกปิดด้วยเหตุผลอื่นอยู่แล้ว) */
function isPast(local: string): boolean {
  const iso = localInputToIso(local);
  return iso !== null && new Date(iso).getTime() <= Date.now();
}

function BackLink() {
  return (
    <Link to="/consultations" className="btn-outline" style={{ display: "inline-flex" }}>
      <ChevronLeft size={14} /> ກັບຄິວນັດປຶກສາ
    </Link>
  );
}
